/**
 * <bar-builder> — BMS multi-scent quantity-tier buy box.
 *
 * State model (deliberately two separate things):
 *   • viewing state  — which scent pill is active. Drives the GALLERY only, by re-dispatching the theme's own
 *                      `variant:change` event on the product form so <product-gallery> reacts natively.
 *   • basket state   — quantity per scent. Only the "Build your box" rows change it. Nothing in the gallery,
 *                      the pills, or a section re-render may clear it.
 *
 * Pricing here is presentation arithmetic. It does not configure Shopify prices, automatic discounts or Skio
 * renewal amounts. While the block's `enable_add_to_cart` setting is off, submit is blocked entirely.
 */
/** Settings are written with their own brackets, e.g. "(Recommended)". Strip them so notes can be combined. */
function stripBrackets(value) {
  return String(value).trim().replace(/^\(|\)$/g, '').trim();
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (char) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[char]);
}

class BarBuilder extends HTMLElement {
  connectedCallback() {
    this.form = document.forms[this.getAttribute('form')];

    this.tiers = JSON.parse(this.getAttribute('data-tiers') || '[]');
    this.subscriptionDiscount = parseFloat(this.getAttribute('data-subscription-discount')) || 0;
    this.moneyFormat = this.getAttribute('data-money-format') || '${{amount}}';
    this.purchasable = this.hasAttribute('data-purchasable');
    this.subscriptionPurchasable = this.hasAttribute('data-subscription-purchasable');

    this.variants = JSON.parse(this.querySelector('[data-bar-builder-variants]')?.textContent || '{}');

    this.rows = Array.from(this.querySelectorAll('[data-scent-row]'));
    this.pills = Array.from(this.querySelectorAll('[data-scent-pill]'));
    this.statusElement = this.querySelector('[data-status]');
    this.activeScentLabel = this.querySelector('[data-active-scent-label]');
    this.ctaTemplate = this.querySelector('[data-cta-template]');
    this.descriptorElement = this.querySelector('[data-scent-descriptor]');
    this.ingredientsAccordion = this.#findIngredientsAccordion();
    this.frequencySelect = this.querySelector('[data-selling-plan]');
    this.monthsPerBar = parseFloat(this.dataset.monthsPerBar) || 1;
    this.recommendedNote = this.dataset.recommendedNote || '(Recommended)';
    // Once the shopper picks a cadence themselves, the recommendation stops moving their selection.
    this.frequencyChosenByShopper = false;

    this.scentLineMode = this.dataset.scentLineMode || 'ingredients';
    this.scentLineLabel = this.dataset.scentLineLabel || '';

    // The full list now sits inline under the pills, so the accordion is redundant here. It is only hidden when
    // every purchasable scent actually has a configured line - otherwise something would silently go missing.
    const everyScentHasDetail = this.rows
      .filter((row) => row.dataset.available === 'true')
      .every((row) => this.variants[row.dataset.variantId]?.hasDetail);

    if (this.ingredientsAccordion && this.scentLineMode !== 'descriptor' && everyScentHasDetail) {
      this.ingredientsAccordion.element.hidden = true;
    }

    this.#openNamedAccordion();

    // Basket: variantId -> quantity. Never reset by viewing state.
    this.quantities = new Map(this.rows.map((row) => [row.dataset.variantId, 0]));

    this.viewedVariantId = this.pills.find((pill) => pill.getAttribute('aria-pressed') === 'true')?.dataset.variantId
      || this.pills[0]?.dataset.variantId
      || null;

    this.#bindEvents();
    this.#moveCta();
    this.#renderScentDetail();
    this.render();
  }

  // ---------------------------------------------------------------------------------------------- events

  #bindEvents() {
    this.addEventListener('click', (event) => {
      const pill = event.target.closest('[data-scent-pill]');
      if (pill && !pill.classList.contains('bar-builder__scent-pill--upcoming')) {
        this.#selectScent(pill.dataset.variantId);
        return;
      }

      const decrease = event.target.closest('[data-qty-decrease]');
      if (decrease) {
        this.#step(decrease.closest('[data-scent-row]'), -1);
        return;
      }

      const increase = event.target.closest('[data-qty-increase]');
      if (increase) {
        this.#step(increase.closest('[data-scent-row]'), 1);
      }
    });

    // Direct numeric entry. `input` keeps the running totals live; `change`/`blur` commits the sanitised value.
    this.addEventListener('input', (event) => {
      const field = event.target.closest('[data-qty-input]');
      if (!field) return;
      this.#commitQuantity(field.closest('[data-scent-row]'), field.value, { rewrite: false });
    });

    this.addEventListener('change', (event) => {
      const field = event.target.closest('[data-qty-input]');
      if (field) {
        this.#commitQuantity(field.closest('[data-scent-row]'), field.value, { rewrite: true });
        return;
      }

      if (event.target.closest('[data-selling-plan]')) {
        this.frequencyChosenByShopper = true;
        return;
      }

      if (event.target.closest('[data-purchase-option]')) {
        this.#moveCta();
        this.render();
      }
    });

    // Block the characters `type=number` otherwise tolerates (e, E, +, -, .) so fractional or negative bars
    // can never reach the basket.
    this.addEventListener('keydown', (event) => {
      if (!event.target.closest('[data-qty-input]')) return;
      if (['e', 'E', '+', '-', '.', ','].includes(event.key)) event.preventDefault();
    });

    this.addEventListener('paste', (event) => {
      if (!event.target.closest('[data-qty-input]')) return;
      const pasted = (event.clipboardData || window.clipboardData).getData('text');
      if (!/^\d+$/.test(pasted.trim())) event.preventDefault();
    });

    this.form?.addEventListener('submit', this.#onSubmit.bind(this));
  }

  // ------------------------------------------------------------------------------------- viewing state

  /**
   * Move the gallery to a scent without touching the basket.
   *
   * Impact builds per-variant image sets with the alt-tag hack, so the media for another scent are `hidden` until the
   * gallery's `filtered-indexes` are refreshed. That refresh only happens on the theme's `product:rerender` event.
   * So this runs the theme's own two-step sequence — fetch the section for the variant, dispatch `product:rerender`,
   * then dispatch `variant:change` — exactly as <variant-picker> does. Selecting the carousel slide directly instead
   * lands on a hidden slide and leaves the gallery with no selection at all.
   *
   * The basket survives this because <product-rerender> only swaps a fixed list of block types (price, buy-buttons,
   * quantity-selector and friends) and `bar-builder` is not one of them.
   */
  async #selectScent(variantId) {
    if (!variantId || variantId === this.viewedVariantId) return;

    const variant = this.variants[variantId];
    if (!variant) return;

    const previousVariantId = this.viewedVariantId;
    const previousVariant = this.variants[previousVariantId] || null;
    this.viewedVariantId = variantId;

    this.pills.forEach((pill) => {
      const selected = pill.dataset.variantId === variantId;
      pill.classList.toggle('is-selected', selected);
      pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    if (this.activeScentLabel) this.activeScentLabel.textContent = variant.title;
    this.rows.forEach((row) => row.classList.toggle('is-focused', row.dataset.variantId === variantId));
    this.#renderScentDetail();

    // Last click wins: a fast run along the pills must not let an earlier fetch land after a later one.
    const token = (this.#navigationToken = (this.#navigationToken || 0) + 1);

    const fragment = await this.#sectionFragmentFor(variantId);
    if (token !== this.#navigationToken) return;

    if (!fragment) {
      // The section rerender did not actually apply (dropped request or a non-2xx response). Dispatching
      // `variant:change` anyway would tell the gallery to move to a slide that `filtered-indexes` was never
      // refreshed to include, which throws inside the theme's own carousel and leaves it stuck. Roll the
      // viewing state back to the previous variant instead, so the pills, label and gallery all agree.
      this.viewedVariantId = previousVariantId;
      this.pills.forEach((pill) => {
        const selected = pill.dataset.variantId === previousVariantId;
        pill.classList.toggle('is-selected', selected);
        pill.setAttribute('aria-pressed', selected ? 'true' : 'false');
      });
      if (this.activeScentLabel && previousVariant) this.activeScentLabel.textContent = previousVariant.title;
      this.rows.forEach((row) => row.classList.toggle('is-focused', row.dataset.variantId === previousVariantId));
      this.#renderScentDetail();
      return;
    }

    this.form?.dispatchEvent(new CustomEvent('product:rerender', {
      detail: { htmlFragment: fragment, productChange: false }
    }));

    this.form?.dispatchEvent(new CustomEvent('variant:change', {
      bubbles: true,
      detail: { formId: this.getAttribute('form'), variant, previousVariant }
    }));
  }

  #navigationToken = 0;
  #sectionCache = new Map();

  async #sectionFragmentFor(variantId) {
    const sectionId = this.dataset.sectionId;
    const handle = this.dataset.productHandle;
    if (!sectionId || !handle) return null;

    try {
      if (!this.#sectionCache.has(variantId)) {
        const url = `${Shopify.routes.root}products/${handle}?variant=${variantId}&section_id=${encodeURIComponent(sectionId)}`;
        const response = await fetch(url);
        // `.text()` resolves even on a 404/500 error page, so a non-2xx must be rejected here explicitly -
        // otherwise an error page gets cached forever as if it were a valid rerender.
        if (!response.ok) return null;
        this.#sectionCache.set(variantId, await response.text());
      }
      // Build a fresh fragment every time: <product-rerender> moves nodes OUT of the fragment it is handed, so a
      // cached fragment would be missing blocks on its second use. The HTML string is what gets cached.
      return document.createRange().createContextualFragment(this.#sectionCache.get(variantId));
    } catch (error) {
      // A failed fetch just means the gallery keeps its current filter; the basket is unaffected either way.
      return null;
    }
  }

  // ------------------------------------------------------------------------------------- scent detail

  /**
   * The Ingredients accordion is a plain collapsible_text block holding every scent's list at once. Rather than
   * duplicate that content into a second block, this takes the existing accordion over and shows the shared base
   * plus the scent being viewed. The original markup is kept and restored whenever this scent has no configured
   * detail, so a typo in the setting can only ever fall back to today's full list, never hide it. With JS off the
   * accordion is simply left as it is.
   */
  #findIngredientsAccordion() {
    const title = (this.dataset.ingredientsTitle || '').trim();
    if (!title) return null;

    const match = Array.from(document.querySelectorAll('.product-info__accordion')).find(
      (accordion) => accordion.querySelector('summary')?.textContent.trim() === title
    );
    if (!match) return null;

    const content = match.querySelector('.accordion__content');
    if (!content) return null;

    return { element: match, content, originalHtml: content.innerHTML };
  }

  /** Open the accordion named in the settings, so the page opens with its most useful drawer already showing. */
  #openNamedAccordion() {
    const title = (this.dataset.openAccordion || '').trim();
    if (!title) return;

    const target = Array.from(document.querySelectorAll('.product-info__accordion')).find(
      (accordion) => !accordion.hidden && accordion.querySelector('summary')?.textContent.trim() === title
    );
    if (!target) return;

    target.open = true;
    target.setAttribute('aria-expanded', 'true');
  }

  #renderScentDetail() {
    const variant = this.variants[this.viewedVariantId];

    if (this.descriptorElement) {
      const descriptor = variant?.descriptor || '';
      const base = this.dataset.ingredientsBase || '';
      const additions = variant?.ingredients || '';
      const fullList = [base, additions].filter(Boolean).join(', ');
      const labelled = `<span class="bar-builder__scent-line-label">${escapeHtml(this.scentLineLabel)}</span> ${escapeHtml(fullList)}`;

      let html = '';
      if (this.scentLineMode === 'descriptor') html = escapeHtml(descriptor);
      else if (this.scentLineMode === 'both') html = `${escapeHtml(descriptor)} ${labelled}`;
      else if (variant?.hasDetail) html = labelled;

      this.descriptorElement.innerHTML = html;
      this.descriptorElement.hidden = html.trim() === '';
    }

    const accordion = this.ingredientsAccordion;
    if (!accordion) return;

    // No configured line for this scent at all: put the original accordion back rather than show less.
    if (!variant?.hasDetail) {
      accordion.content.innerHTML = accordion.originalHtml;
      return;
    }

    // One list, reading the way the bar is actually made: the shared base first, then whatever this scent adds.
    // A scent with no additions (Unscented) is simply the base.
    const base = this.dataset.ingredientsBase || '';
    const additions = variant.ingredients || '';
    const full = [base, additions].filter(Boolean).join(', ');

    accordion.content.innerHTML = `
      <div class="prose bar-builder__ingredients">
        <p><strong>${escapeHtml(variant.title)}</strong><br>${escapeHtml(full)}</p>
      </div>
    `;
  }

  // -------------------------------------------------------------------------------------- basket state

  #step(row, delta) {
    if (!row) return;
    const current = this.quantities.get(row.dataset.variantId) || 0;
    this.#commitQuantity(row, current + delta, { rewrite: true });
    this.#selectScent(row.dataset.variantId);
  }

  #commitQuantity(row, rawValue, { rewrite }) {
    if (!row || row.dataset.available !== 'true') return;

    let quantity;
    if (typeof rawValue === 'number') {
      quantity = Number.isFinite(rawValue) ? Math.trunc(rawValue) : 0;
    } else {
      // Only a plain run of digits counts. "-1", "2.7", "1e3" and "abc" are rejected outright rather than coerced,
      // so a stray minus can never flip to a positive quantity and a decimal can never inflate into a bigger integer.
      const text = String(rawValue).trim();
      quantity = /^\d+$/.test(text) ? parseInt(text, 10) : 0;
    }
    quantity = Math.min(Math.max(quantity, 0), 999);

    this.quantities.set(row.dataset.variantId, quantity);

    // While typing, never rewrite the field: the caret would jump. The sanitised value is written back on
    // change/blur and on every +/- step.
    if (rewrite) {
      const field = row.querySelector('[data-qty-input]');
      if (field) field.value = String(quantity);
    }

    row.classList.toggle('has-quantity', quantity > 0);
    this.render();
  }

  // ------------------------------------------------------------------------------------------- pricing

  get totalBars() {
    let total = 0;
    this.quantities.forEach((quantity) => { total += quantity; });
    return total;
  }

  get activeTierIndex() {
    const bars = this.totalBars;
    if (bars < 1) return -1;
    return this.tiers.findIndex((tier) => tier.max === null || bars <= tier.max);
  }

  get unitPrice() {
    const index = this.activeTierIndex;
    return index === -1 ? 0 : this.tiers[index].price;
  }

  get oneTimeTotal() {
    return this.totalBars * this.unitPrice;
  }

  get subscriptionTotal() {
    return Math.round(this.oneTimeTotal * (100 - this.subscriptionDiscount) / 100);
  }

  get selectedPurchaseOption() {
    return this.querySelector('[data-purchase-option]:checked')?.value || 'onetime';
  }

  get selectedSellingPlanId() {
    if (this.selectedPurchaseOption !== 'subscription') return null;
    const value = this.querySelector('[data-selling-plan]')?.value;
    return value ? parseInt(value, 10) : null;
  }

  formatMoney(cents) {
    const value = (cents / 100);
    const withDecimals = value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const noDecimals = Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    return this.moneyFormat
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withDecimals.replace(/,/g, 'X').replace('.', ',').replace(/X/g, '.'))
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals.replace(/,/g, '.'));
  }

  // -------------------------------------------------------------------------------------------- render

  render() {
    const bars = this.totalBars;
    const activeTier = this.activeTierIndex;

    this.querySelectorAll('[data-tier-index]').forEach((element) => {
      element.classList.toggle('is-active', parseInt(element.dataset.tierIndex, 10) === activeTier);
    });

    const onetimePrice = this.querySelector('[data-onetime-price]');
    const subscriptionPrice = this.querySelector('[data-subscription-price]');

    if (onetimePrice) onetimePrice.textContent = bars > 0 ? this.formatMoney(this.oneTimeTotal) : '—';
    if (subscriptionPrice) subscriptionPrice.textContent = bars > 0 ? this.formatMoney(this.subscriptionTotal) : '—';

    this.querySelectorAll('.bar-builder__option').forEach((option) => {
      option.classList.toggle('is-selected', option.dataset.option === this.selectedPurchaseOption);
    });

    this.toggleAttribute('data-empty', bars === 0);
    this.#renderRecommendedFrequency();

    const submit = this.querySelector('[data-submit]');
    const label = this.querySelector('[data-submit-label]');
    if (submit) {
      // A subscription selection that never resolved to a real Skio plan must block submit exactly like an
      // empty basket does - otherwise it silently posts a one-time cart line under a "Subscribe & Save" label.
      const noPlanSelected = this.selectedPurchaseOption === 'subscription' && !this.selectedSellingPlanId;
      const subscriptionBlocked = this.selectedPurchaseOption === 'subscription' && !this.subscriptionPurchasable;
      const blocked = bars === 0 || !this.purchasable || noPlanSelected || subscriptionBlocked || this.submitting;
      submit.disabled = blocked;
      if (label) {
        if (bars === 0) {
          label.textContent = submit.dataset.emptyLabel || 'SELECT AT LEAST ONE BAR';
        } else if (noPlanSelected) {
          label.textContent = 'CHOOSE A DELIVERY FREQUENCY';
        } else if (subscriptionBlocked) {
          label.textContent = 'SUBSCRIPTION PRICING IN TESTING';
        } else {
          label.textContent = submit.dataset.defaultLabel;
        }
      }
    }
  }

  /**
   * Point the shopper at a cadence that matches how much soap they are actually buying: more bars, longer gap.
   * The marker always follows the basket; the SELECTION only follows until the shopper picks a cadence themselves,
   * and only ever lands on a month that has a real Skio plan behind it.
   */
  #renderRecommendedFrequency() {
    if (!this.frequencySelect) return;

    const options = Array.from(this.frequencySelect.options);
    // Only a month with a real Skio plan behind it can ever be "Recommended" or auto-selected. This store has
    // plans for a handful of months only, so most baskets round to a cadence nothing can sell; walk to the
    // nearest CONFIGURED month instead of clamping onto an unconfigured one.
    const configured = options.filter((option) => !option.hasAttribute('data-unconfigured'));
    const bars = this.totalBars;
    const rawMonths = bars === 0 ? 0 : Math.max(Math.round(bars * this.monthsPerBar), 1);

    let recommended = null;
    if (rawMonths > 0 && configured.length > 0) {
      recommended = configured.reduce((closest, option) => {
        const optionMonths = parseInt(option.dataset.months, 10);
        const closestMonths = parseInt(closest.dataset.months, 10);
        return Math.abs(optionMonths - rawMonths) < Math.abs(closestMonths - rawMonths) ? option : closest;
      }, configured[0]);
    }

    options.forEach((option) => {
      const isRecommended = option === recommended;

      // One parenthetical, never two stacked next to each other.
      const base = option.dataset.label || option.textContent.trim();
      const notes = [];
      if (isRecommended) notes.push(stripBrackets(this.recommendedNote));
      if (option.hasAttribute('data-unconfigured')) notes.push(stripBrackets(this.dataset.unconfiguredNote || ''));
      option.textContent = notes.length ? `${base} (${notes.filter(Boolean).join(', ')})` : base;
    });

    if (recommended && recommended.value && !this.frequencyChosenByShopper) {
      this.frequencySelect.value = recommended.value;
    }
  }

  /** Keep one CTA in the DOM, inside whichever purchase option is selected (matches the approved reference). */
  #moveCta() {
    const target = this.querySelector(`.bar-builder__option[data-option="${this.selectedPurchaseOption}"] [data-cta-slot]`);
    if (!target) return;

    let cta = this.querySelector('[data-submit]');
    if (!cta) {
      cta = this.ctaTemplate.content.firstElementChild.cloneNode(true);
      cta.dataset.defaultLabel = cta.querySelector('[data-submit-label]').textContent.trim();
      cta.dataset.emptyLabel = this.dataset.emptyLabel || 'SELECT AT LEAST ONE BAR';
    }
    target.appendChild(cta);
  }

  // ----------------------------------------------------------------------------------------- cart add

  /** One cart line per scent with a positive quantity. Never an order note, never a single blended line. */
  get cartItems() {
    const sellingPlanId = this.selectedSellingPlanId;

    return this.rows
      .filter((row) => row.dataset.available === 'true' && (this.quantities.get(row.dataset.variantId) || 0) > 0)
      .map((row) => {
        const item = {
          id: parseInt(row.dataset.variantId, 10),
          quantity: this.quantities.get(row.dataset.variantId)
        };

        const eligiblePlans = (row.dataset.sellingPlanIds || '').split(',').filter(Boolean);
        if (sellingPlanId && eligiblePlans.includes(String(sellingPlanId))) {
          item.selling_plan = sellingPlanId;
        }
        return item;
      });
  }

  async #onSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;

    let items = this.cartItems;
    if (items.length === 0) {
      this.#setStatus('Choose at least one bar.', 'error');
      return;
    }

    // Belt and braces on top of the `disabled` options and the `blocked` render() state: never post a
    // subscription cart where a line ended up with no selling_plan attached. That is a silent one-time order
    // under a "Subscribe & Save" label, not a discount or a price mistake - the whole subscription is gone.
    if (this.selectedPurchaseOption === 'subscription' && items.some((item) => !item.selling_plan)) {
      this.#setStatus("That delivery frequency isn't available yet - pick another to subscribe.", 'error');
      return;
    }

    if (!this.purchasable) {
      this.#setStatus(
        `Preview only — add to cart is switched off for this draft. This selection would post ${items.length} cart line${items.length === 1 ? '' : 's'}: `
        + items.map((item) => `${item.quantity} × ${item.id}${item.selling_plan ? ` (plan ${item.selling_plan})` : ''}`).join(', ')
        + '.',
        'note'
      );
      return;
    }

    if (this.selectedPurchaseOption === 'subscription' && !this.subscriptionPurchasable) {
      this.#setStatus('Subscription checkout is disabled until quantity pricing is verified.', 'note');
      return;
    }

    const submit = this.querySelector('[data-submit]');
    this.submitting = true;
    this.render();
    submit?.setAttribute('aria-busy', 'true');

    const sectionsToBundle = ['variant-added'];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sectionsToBundle }
    }));

    try {
      // Plain Shopify variant + selling plan lines. Skio's native volume rules
      // calculate both checkout and recurring tiers from eligible soap quantity.
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items, sections: sectionsToBundle.join(','), sections_url: window.location.pathname })
      });

      const payload = await response.json();

      if (!response.ok) {
        this.#setStatus(payload.description || payload.message || 'Could not add to cart.', 'error');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        return;
      }

      const cart = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cart.sections = payload.sections;

      this.#setStatus('', null);
      this.form.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: { items: payload.items || [payload], cart }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: { baseEvent: 'variant:add', cart }
      }));
    } catch (error) {
      this.#setStatus(error.nativeBoxMessage ? error.message : 'Could not reach the cart. Try again.', 'error');
    } finally {
      this.submitting = false;
      this.render();
      submit?.removeAttribute('aria-busy');
    }
  }

  #setStatus(message, kind) {
    if (!this.statusElement) return;
    this.statusElement.textContent = message;
    this.statusElement.hidden = !message;
    this.statusElement.dataset.kind = kind || '';
  }
}

if (!window.customElements.get('bar-builder')) {
  window.customElements.define('bar-builder', BarBuilder);
}
